import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { compose, defaultProps } from 'recompose';
import DropdownList from 'react-widgets/lib/DropdownList';
import {
  Button,
  Glyphicon,
  InputGroup,
} from 'react-bootstrap';
import createAdapter from '../../../utils/fuseFilterAdapter';
import LookupResultListItem from '../../lookups/components/LookupResultListItem';
import CompatLookupResultListItem from '../../lookups/components/CompatLookupResultListItem';
import { lookupJoinTypePropType } from '../../../propTypes';
import { querySelector } from '../../../utils';
import createOption from '../../../utils/dropdownListCreateOption';


/* eslint-disable react/forbid-prop-types, react/require-default-props */
const propTypes = {
  leases: PropTypes.array.isRequired,
  operator: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  disabled: PropTypes.bool.isRequired,
  lookupJoinType: lookupJoinTypePropType.isRequired,
  createLease: PropTypes.func.isRequired,
  createLeaseFromLookup: PropTypes.func.isRequired,
  data: PropTypes.object,
  allowCreate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};
/* eslint-enable react/forbid-prop-types, react/require-default-props */

export class LeaseInput extends Component {
  constructor(props) {
    super(props);

    this.handleCreate = this.handleCreate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCreateFromLookup = this.handleCreateFromLookup.bind(this);
    this.maybeCreateFromLookup = this.maybeCreateFromLookup.bind(this);
  }

  async handleCreate(name) {
    const { onChange } = this.props;
    const operatorId = this.props.operator.user_id;
    const variables = {
      input: {
        name,
        operator_id: operatorId,
      },
    };
    try {
      const result = await this.props.createLease({ variables });
      await this.props.data.refetch();
      const lease = result.data.lease_create;
      onChange(lease);
    } catch (e) {
      console.log('error', e);
    }
  }

  async handleCreateFromLookup(lookupId) {
    const variables = {
      input: {
        lease_lookup_id: lookupId,
      },
    };

    const result = await this.props.createLeaseFromLookup({ variables });
    await this.props.data.refetch();
    return result.data.lookup_result;
  }

  async maybeCreateFromLookup(maybeLookup) {
    if (!maybeLookup) {
      return null;
    }

    if (maybeLookup.lookupStatus === 'reference') {
      const newRecord = await this.handleCreateFromLookup(maybeLookup.lookup_id);
      return newRecord;
    }

    return maybeLookup;
  }

  async handleChange(data) {
    const record = await this.maybeCreateFromLookup(data);

    this.props.onChange(record);
  }

  render() {
    const {
      leases, value, disabled, onBlur, onFocus, lookupJoinType, allowCreate,
    } = this.props;

    const filter = createAdapter(leases);
    const itemComponent = lookupJoinType === 'union' ? LookupResultListItem : CompatLookupResultListItem;

    return (
      <div>
        <InputGroup>
          <DropdownList
            disabled={disabled}
            inputProps={{
              className: 'select',
            }}
            filter={filter}
            allowCreate={allowCreate}
            data={leases}
            value={value}
            valueField="id"
            textField="name"
            messages={{ createOption }}
            itemComponent={itemComponent}
            onCreate={(name) => { return this.handleCreate(name); }}
            onChange={this.handleChange}
            onFocus={onFocus}
            onBlur={() => { onBlur(); }}
          />
          <InputGroup.Button>
            <Button
              name="cancel"
              disabled={!value || disabled}
              /* calling onBlur event to change touched flag to show error if need be */
              onClick={() => {
                onBlur();
                this.handleChange(null);
              }}
            >
              <Glyphicon glyph="remove" />
            </Button>
          </InputGroup.Button>
        </InputGroup>
      </div>
    );
  }
}

LeaseInput.propTypes = propTypes;
LeaseInput.defaultProps = {
  allowCreate: 'onFilter',
};

export const LeaseInputLeaseLookupFragment = gql`
  fragment LeaseInputLeaseLookup on LeaseLookupResult {
    id
    user_id
    lookup_id
    name
    lookupStatus: lookup_status
    lookup_status
  }
`;

export const LeaseInputLeaseFragment = gql`
  fragment LeaseInputLease on LeaseLookupResult {
    id
    user_id
    lookup_id
    name
    lookupStatus: lookup_status
    lookup_status
    lease {
      id
      operator {
        id
      }
    }
  }
`;

const GetAllLeasesQuery = gql`
  query GetAllLeases($operatorId: Int, $lookupJoinType: LookupJoinTypes!) {
    all_leases: lookup_leases(operator_id: $operatorId, lookup_query: {join_type: $lookupJoinType}) {
      edges {
        node {
          ...LeaseInputLease
        }
      }
    }
  }
  ${LeaseInputLeaseFragment}
`;

const CreateLeaseMutation = gql`
  mutation CreateLeaseMutation($input: LeaseCreateFromNameInput!) {
    lease_create: lease_create_from_name(input: $input) {
      ...LeaseInputLease
    }
  }
  ${LeaseInputLeaseFragment}
`;

const CreateLeaseFromLookupMutation = gql`
  mutation LeaseCreateFromLookup($input: LeaseCreateFromLookupInput!) {
    lookup_result: lease_create_from_lookup(input: $input) {
      ...LeaseInputLease
    }
  }
  ${LeaseInputLeaseFragment}
`;

export const marshallData = ({ data, ownProps }) => {
  const leases = querySelector(data, '$.all_leases.edges[*].node', []);
  return {
    ...ownProps,
    data,
    leases,
  };
};

const queryVariables = (props) => {
  const operatorId = (props.operator) && props.operator.user_id;
  const { lookupJoinType } = props;
  return {
    variables: {
      operatorId,
      lookupJoinType,
    },
  };
};

const withData = graphql(GetAllLeasesQuery, {
  props: marshallData,
  options: (props) => {
    return {
      variables: queryVariables(props).variables,
      skip: !props.operator || !props.operator.user_id,
    };
  },
});

const withCreate = graphql(CreateLeaseMutation, { name: 'createLease' });

const withCreateFromLookup = graphql(CreateLeaseFromLookupMutation, { name: 'createLeaseFromLookup' });

const enhance = compose(
  defaultProps({ lookupJoinType: 'user_records' }),
  withData,
  withCreate,
  withCreateFromLookup,
);

export default enhance(LeaseInput);
